import React, { FunctionComponent } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { useHistory } from 'react-router-dom';
import { BASE_URL } from '../../../routes';
import { getFullAddress } from '../../../utils/utils';
import { ApplicationList, Application } from '../../../store/modules/application/types';
import { Person } from '../../../store/modules/person/types';

type LicenseListSearchedProps = {
    licenseListSearched: ApplicationList;
    isLoading: boolean;
    resetSearchField: Function;
    highlightedIndex: number;
    personListSearched: Person[];
    isPersonLoading: boolean;
};

const useStyles = makeStyles({
    licenseListContainer: { width: '100%', overflow: 'clip scroll', maxHeight: 300 },
    licenseNodeContainer: {
        color: 'gray',
        padding: '0px 16px',
        '&:hover ': {
            cursor: 'pointer',
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
        display: 'flex',
        flexDirection: 'column',
    },
    highlighted: {
        backgroundColor: '#eeeeee',
        transition: 'ease-in 200ms',
        '& button': {
            opacity: 1,
            transition: 'ease-in 200ms',
        },
    },
    centered: {
        display: 'flex',
        justifyContent: 'center',
    },
    licenseNodeContentLeft: {
        minWidth: 150,
        padding: '20px 0px',
        color: '#6E7788',
        fontSize: 14,
        letterSpacing: 0.1,
    },
    licenseNodeContentRight: {
        minWidth: 150,
        padding: '20px 0px',
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
    },
    hr: {
        border: '0.1px solid #EEEEEE',
        width: '100%',
    },
    loaderContainer: {
        margin: 10,
        overflow: 'hide hide',
        height: 'auto',
        display: 'flex',
        justifyContent: 'center',
    },
    searchIconContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
        display: 'flex',
        flexDirection: 'column',
    },
    searchIcon: {
        fontSize: 70,
        color: '#CBCBCB',
    },
    noResultFound: {
        fontSize: '20px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 500,
        marginTop: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
    },
    noResultFoundMessage1: {
        fontSize: '16px',
        color: '#484848',
        lineHeight: '24px',
        marginTop: '1rem',
        fontWeight: 400,
        display: 'flex',
        justifyContent: 'center',
    },
    noResultFoundMessage2: {
        fontSize: '16px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 400,
        display: 'flex',
        justifyContent: 'center',
    },
    resultContent: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    PersonNodeContentLeft: {
        minWidth: 150,
        width: '200px',
        padding: '20px 0px',
        color: '#6E7788',
        fontSize: 14,
        letterSpacing: 0.1,
    },
    PersonNodeContentRight: {
        minWidth: 150,
        width: '200px',
        padding: '20px 0px',
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        justifyContent: 'flex-end',
    },
    PersonName: {
        fontWeight: 500,
        color: '#0277BD',
    },
    PersonAddressContainer: {
        color: '#6E7788',
        fontSize: '12px',
        width: '200px',
    },
    PersonAddress: {
        width: '100%',
    },
    LicenseInfo: {
        color: '#484848',
        fontWeight: 500,
    },
    LicenseAddressContainer: {
        color: '#6E7788',
        fontSize: '12px',
        wordBreak: 'break-all',
    },
    LicenseAddress: {
        width: '100%',
    },
});

const LicenseListSearched: FunctionComponent<LicenseListSearchedProps> = (props) => {
    const classes = useStyles();
    const history = useHistory();

    const routeChange = (path: string) => {
        history.push(path);
    };

    const {
        isLoading,
        licenseListSearched,
        resetSearchField,
        highlightedIndex,
        personListSearched,
        isPersonLoading,
    } = props;

    return (
        <>
            {(isLoading || isPersonLoading) && (
                <div className={classes.loaderContainer}>
                    <CircularProgress />
                </div>
            )}

            {!isLoading &&
            licenseListSearched &&
            licenseListSearched.length === 0 &&
            personListSearched?.length === 0 ? (
                <div className={classes.searchIconContainer}>
                    <div className={classes.centered}>
                        <SearchIcon className={classes.searchIcon} />
                    </div>
                    <div className={classes.noResultFound}>No Result Found</div>
                    <div className={classes.noResultFoundMessage1}>
                        We couldn’t find what you’re searching for.
                    </div>{' '}
                    <div className={classes.noResultFoundMessage2}>Try again using different terms.</div>
                </div>
            ) : (
                <div className={classes.licenseListContainer}>
                    {(!isLoading || !isPersonLoading) && (
                        <>
                            {licenseListSearched.map((license: Application, index) => {
                                const personAddress = getFullAddress(license?.person?.address);
                                const licenseAddress = getFullAddress(license?.address);
                                return (
                                    <div
                                        key={license.id}
                                        className={[
                                            classes.licenseNodeContainer,
                                            highlightedIndex === index ? classes.highlighted : '',
                                        ].join(' ')}
                                    >
                                        {index !== 0 && (
                                            <div>
                                                <div className={classes.hr}></div>
                                            </div>
                                        )}
                                        <div className={classes.resultContent}>
                                            <div
                                                className={classes.PersonNodeContentLeft}
                                                onClick={() => {
                                                    routeChange(BASE_URL + `/person/${license.person.id}/detail`);
                                                    resetSearchField();
                                                }}
                                            >
                                                <div className={classes.PersonName}>
                                                    {typeof license['person']['name'] === 'string'
                                                        ? license['person']['name']
                                                        : `${license.person?.name?.firstName} ${license.person?.name?.lastName}`}
                                                </div>
                                                <div className={classes.PersonAddressContainer}>
                                                    <div className={classes.PersonAddress}>{personAddress}</div>
                                                </div>
                                            </div>
                                            <div
                                                className={classes.PersonNodeContentRight}
                                                onClick={() => {
                                                    // eslint-disable-next-line no-console
                                                    console.log('LICENSE', license.id);

                                                    // if (license.id === '63e40a5a776d41004a0459d1') {
                                                    //     throw new Error();
                                                    // }

                                                    routeChange(BASE_URL + `/license/${license.id}/detail`);

                                                    resetSearchField();
                                                }}
                                            >
                                                <div className={classes.LicenseInfo}>
                                                    {license?.additionalData?.rideName &&
                                                        license?.licenseNumber &&
                                                        license.licenseNumber + ' • ' + license.additionalData.rideName}
                                                    {!license?.additionalData?.rideName &&
                                                        license?.licenseNumber &&
                                                        license.licenseNumber}
                                                </div>
                                                <div className={classes.LicenseAddressContainer}>
                                                    <div className={classes.LicenseAddress}>{licenseAddress}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {personListSearched?.map((user: Person, index) => (
                                <div
                                    key={user.id}
                                    className={[
                                        classes.licenseNodeContainer,
                                        highlightedIndex === index ? classes.highlighted : '',
                                    ].join(' ')}
                                >
                                    {index !== 0 && (
                                        <div>
                                            <div className={classes.hr}></div>
                                        </div>
                                    )}
                                    <div className={classes.resultContent}>
                                        <div
                                            className={classes.PersonNodeContentLeft}
                                            onClick={() => {
                                                routeChange(BASE_URL + `/person/${user.id}/detail`);
                                                resetSearchField();
                                            }}
                                        >
                                            <div className={classes.PersonName}>
                                                {typeof user['name'] === 'string'
                                                    ? user['name']
                                                    : `${user.name?.firstName} ${user.name?.lastName}`}
                                            </div>
                                            <div className={classes.PersonAddressContainer}>
                                                <div className={classes.PersonAddress}>
                                                    {user?.address && getFullAddress(user?.address)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </>
    );
};
export default LicenseListSearched;
